/**
 * useVideoThumbnail
 *
 * Returns a thumbnail URL for a video event, with a multi-tier strategy:
 *
 *  1. Stored thumbnail from the event's imeta/thumb tag  → returned immediately
 *  2. localStorage URL cache (keyed by event ID)         → returned immediately
 *  3. Frame capture + Blossom upload (logged-in users)   → async, permanent URL cached
 *     Frame capture + localStorage data URI (guests)     → async, local cache only
 *
 * Captures are deduped across cards — if 10 VideoCards for the same event are
 * mounted at once, only one <video> element is created; all hooks share the same
 * Promise and receive the result together.
 *
 * For logged-in users the generated thumbnail is uploaded to the user's configured
 * Blossom servers so it is accessible across devices and browsers. The resulting
 * public URL is cached in localStorage (just the URL string, not the image data)
 * so subsequent loads skip everything.
 *
 * For guests the JPEG data URI is stored directly in localStorage (up to 300
 * entries, LRU-evicted) as a fallback.
 */

import { useState, useEffect } from 'react';
import { BlossomUploader } from '@nostrify/nostrify/uploaders';
import type { NUser } from '@nostrify/react/login';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useAppContext } from '@/hooks/useAppContext';
import { getEffectiveBlossomServers } from '@/lib/appBlossom';

// ── Constants ─────────────────────────────────────────────────────────────────

const CACHE_KEY_PREFIX = 'gb:thumb:';
const MAX_CACHE_ENTRIES = 300;
const THUMB_WIDTH = 640;
const THUMB_HEIGHT = 360;
const THUMB_QUALITY = 0.75;
const SEEK_FRACTION = 0.2;

// ── Module-level in-flight dedup map ─────────────────────────────────────────

/** videoUrl → pending Promise<url | dataUri> */
const inflight = new Map<string, Promise<string>>();

// ── localStorage helpers ──────────────────────────────────────────────────────

function cacheGet(eventId: string): string | null {
  try {
    return localStorage.getItem(CACHE_KEY_PREFIX + eventId);
  } catch {
    return null;
  }
}

function cacheSet(eventId: string, value: string): void {
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k?.startsWith(CACHE_KEY_PREFIX)) keys.push(k);
    }
    if (keys.length >= MAX_CACHE_ENTRIES) {
      const toRemove = keys.slice(0, keys.length - MAX_CACHE_ENTRIES + 1);
      toRemove.forEach(k => localStorage.removeItem(k));
    }
    localStorage.setItem(CACHE_KEY_PREFIX + eventId, value);
  } catch {
    // localStorage full or unavailable — silently skip
  }
}

// ── Frame capture ─────────────────────────────────────────────────────────────

/** Captures a frame from videoUrl and returns a JPEG data URI. */
function captureFrameDataUri(videoUrl: string): Promise<string> {
  const existing = inflight.get(videoUrl);
  if (existing) return existing;

  const promise = new Promise<string>((resolve, reject) => {
    const vid = document.createElement('video');
    vid.crossOrigin = 'anonymous';
    vid.preload = 'metadata';
    vid.muted = true;

    vid.addEventListener('loadedmetadata', () => {
      const dur = vid.duration;
      vid.currentTime = isFinite(dur) && dur > 0 ? dur * SEEK_FRACTION : 0;
    });

    vid.addEventListener('seeked', () => {
      try {
        const srcW = vid.videoWidth || 1280;
        const srcH = vid.videoHeight || 720;
        const scale = Math.min(THUMB_WIDTH / srcW, THUMB_HEIGHT / srcH);
        const dstW = Math.round(srcW * scale);
        const dstH = Math.round(srcH * scale);

        const canvas = document.createElement('canvas');
        canvas.width = dstW;
        canvas.height = dstH;
        const ctx = canvas.getContext('2d');
        if (!ctx) { reject(new Error('No 2d context')); return; }
        ctx.drawImage(vid, 0, 0, dstW, dstH);
        resolve(canvas.toDataURL('image/jpeg', THUMB_QUALITY));
      } catch (err) {
        reject(err);
      }
    });

    vid.addEventListener('error', () => reject(new Error('Video load error')));
    vid.src = videoUrl;
  }).finally(() => {
    inflight.delete(videoUrl);
  });

  inflight.set(videoUrl, promise);
  return promise;
}

/** Converts a base64 data URI to a File object for Blossom upload. */
function dataUriToFile(dataUri: string, filename: string): File {
  const [header, base64] = dataUri.split(',');
  const mime = header.match(/:(.*?);/)?.[1] ?? 'image/jpeg';
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new File([bytes], filename, { type: mime });
}

/** Upload a data URI to Blossom and return the public URL. */
async function uploadToBlossom(
  dataUri: string,
  servers: string[],
  user: NUser,
): Promise<string> {
  const file = dataUriToFile(dataUri, 'thumbnail.jpg');
  const uploader = new BlossomUploader({ servers, signer: user.signer });
  const tags = await uploader.upload(file);
  const url = tags.find(([k]) => k === 'url')?.[1];
  if (!url) throw new Error('Blossom did not return a URL');
  return url;
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useVideoThumbnail(
  eventId: string,
  storedThumbnail: string | undefined,
  videoUrl: string | undefined,
): string | null {
  const { user } = useCurrentUser();
  const { config } = useAppContext();

  // Tier 1: event already has a thumbnail
  // Tier 2: check localStorage synchronously (useState initialiser = before first paint)
  const [thumbSrc, setThumbSrc] = useState<string | null>(() => {
    if (storedThumbnail) return storedThumbnail;
    if (!eventId) return null;
    return cacheGet(eventId);
  });

  useEffect(() => {
    // Nothing to do if the event hasn't loaded, thumbnail is already known, or no video URL
    if (!eventId || storedThumbnail || !videoUrl) return;

    // Already in cache — nothing to do
    const cached = cacheGet(eventId);
    if (cached) {
      setThumbSrc(cached);
      return;
    }

    let cancelled = false;

    const run = async () => {
      // Step 1: capture frame as data URI
      const dataUri = await captureFrameDataUri(videoUrl);
      if (cancelled) return;

      // Step 2a: logged-in → upload to Blossom, cache the public URL
      if (user) {
        try {
          const servers = getEffectiveBlossomServers(
            config.blossomServerMetadata,
            config.useAppBlossomServers,
          );
          if (servers.length > 0) {
            const publicUrl = await uploadToBlossom(dataUri, servers, user);
            if (cancelled) return;
            cacheSet(eventId, publicUrl);
            setThumbSrc(publicUrl);
            return;
          }
        } catch {
          // Blossom upload failed — fall through to data URI cache below
        }
      }

      // Step 2b: guest or Blossom failed → store data URI locally
      if (cancelled) return;
      cacheSet(eventId, dataUri);
      setThumbSrc(dataUri);
    };

    run().catch(() => { /* silently ignore */ });

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, videoUrl, !!user]);

  // If storedThumbnail arrives after mount (event loaded async), sync it
  useEffect(() => {
    if (storedThumbnail) {
      setThumbSrc(storedThumbnail);
    }
  }, [storedThumbnail]);

  return thumbSrc;
}
